<template>
  <b-overlay :show="busy">
    <div class="container-fluid" id="order-schedule">
      <div class="row mt-3 mb-4">
        <div class="col-lg-4 col-md-12">
          <h3>Order Schedule</h3>
        </div>
        <div class="col-lg-8 col-md-12">
          <b-input-group>
            <b-form-input
              v-model="query"
              type="text"
              block
              placeholder="Search ..."
            />
            <b-input-group-append>
              <b-input-group-text
                v-if="query"
                @click="query = ''"
                class="icon-close-append"
                title="Clear"
              >
                <i class="icon-close"></i>
              </b-input-group-text>
            </b-input-group-append>
          </b-input-group>
        </div>
      </div>
      <div class="row">
        <board-card
          v-for="board in filterredBoards"
          :key="board.key"
          :id="board.key"
          :title="`${board.title} ( ${board.items.length} )`"
          :group="board.group"
          :variant="board.variant"
          :items="board.items"
          :lg="3"
          :md="12"
          @onMoveOrder="moveOrder"
          @onClickOrder="(o) => clickOrder(o, board.key)"
        >
          <template v-slot:header-action v-if="isAssigned(board)">
            <button class="btn btn-sm btn-primary btn-create" @click="onClickCreateShipment">Create</button>
          </template>
          <template
            v-slot:body-content
            v-if="isAssigned(board) && isAssignedNotEmpty(board)"
          >
            <board-card
              v-for="item in board.items"
              :key="item.shipment_id"
              :id="`assigned-${item.shipment_id}`"
              :title="item.title"
              :group="item.group"
              :items="item.items"
              :lg="12"
              :md="12"
              @onMoveOrder="moveOrder"
              @onClickOrder="(o) => clickOrder(o, board.key)"
            >
              <template v-slot:header v-if="item.items.length">
                <b-badge
                  variant="primary"
                  class="job-counter"
                >
                  {{ item.items.length }} Jobs
                </b-badge>
              </template>
              <template v-slot:header-subtitle>
                <small>{{ getDriverName(item) }}</small>
                <b-badge class="crew" v-if="getCrew(item)" v-b-popover.hover="getCrew(item, false)">
                  {{ getCrew(item) }}
                </b-badge>
              </template>
              <template v-slot:header-action>
                <b-badge variant="info">{{ getShipmentState(item) }}</b-badge>
                <small v-html="getDeliveryDate(item)"></small>
                <b-dropdown
                  size="lg"
                  class="dropdown-button"
                  variant="link"
                  toggle-class="text-decoration-none"
                  no-caret
                >
                  <template #button-content>
                    <span title="Action" class="fa fa-ellipsis-v"></span>
                  </template>
                  <b-dropdown-item @click="onClickEditShipment(item)">
                    <i class="text-primary fa fa-pencil-square-o"></i> Edit
                  </b-dropdown-item>
                  <b-dropdown-item @click="onClickDeleteShipment(item)">
                    <i class="text-danger fa fa-trash-o"></i> Delete
                  </b-dropdown-item>
                </b-dropdown>
              </template>
            </board-card>
          </template>
        </board-card>
      </div>
    </div>
    <b-modal
      scrollable
      hide-footer
      size="lg"
      ref="orderDetails"
    >
      <div slot="modal-title">
        <h5 style="font-weight: bold; margin-bottom: 0px;">
          {{ getAddressLine() }}
          <b-badge
            :class="`bg-${boardVariant}`
          ">
            {{ boardTitle }}
          </b-badge>
          <b-badge variant="danger" v-if="showExtra()">{{ getExtra() }}</b-badge>
        </h5>
        <div class="advance--order-detail mt-2" v-if="['completed', 'rejected'].includes(boardKey)">
          <span class="text-muted" v-if="get(selectedOrder, 'shipment.vehicle.reg_no', '')" title="Vehicle">
            <i class="fa fa-car" aria-hidden="true"></i> {{ get(selectedOrder, 'shipment.vehicle.reg_no', '') }}
          </span>
          <span class="text-muted ml-2" v-if="get(selectedOrder, 'shipment.driver.name', '')" title="Driver">
            <i class="fa fa-id-card" aria-hidden="true"></i> {{ get(selectedOrder, 'shipment.driver.name', '') }}
          </span>
        </div>
      </div>
      <div class="container text-md-left">
        <div class="row">
          <div class="col-md-10 pr-4">
            <div class="row mb-2">
              <div class="col-md-6 p-0">
                <h5 style="font-weight: bold;">Order Number</h5>
                <p style="margin-bottom: 5px;">{{ get(selectedOrder, 'number', '-') }}</p>
              </div>
            </div>
            <div class="row mb-2">
              <div class="col p-0">
                <h5 style="font-weight: bold;">Work Order Description</h5>
                <p style="margin-bottom: 5px;">{{ get(selectedOrder, 'metadata.work_order_description', '-') }}</p>
              </div>
            </div>
            <div class="row mb-2">
              <div class="col p-0">
                <h5 style="font-weight: bold;">Work Order Detail</h5>
                <table>
                  <tr
                    v-for="(detail, idx) in get(selectedOrder, 'metadata.work_order_details_parse', [])"
                    :key="`detail-${detail.key}-${idx}`"
                  >
                    <td style="vertical-align: top;">
                      <strong>{{ detail.key }}</strong>
                    </td>
                    <td style="display: inline-block; word-break: break-word;">
                      <a :href="detail.value" target="_blank" v-if="isValidHttpUrl(detail.value)">
                        {{ detail.value }}
                      </a>
                      <span v-else>
                        {{ detail.value }}
                      </span>
                    </td>
                  </tr>
                </table>
              </div>
            </div>
            <div class="row mb-4">
              <div class="col p-0">
                <h5 style="font-weight: bold;">Location</h5>
                <p v-if="mapState === 'loading'">Please Wait</p>
                <l-map
                  v-else
                  ref="map"
                  @ready="onMapReady"
                  :style="getMapStyles()"
                  :bounds="bounds"
                  :zoom.sync="zoom"
                  :center="center"
                >
                  <l-tile-layer :url="url"></l-tile-layer>
                  <l-marker
                    :lat-lng="getMarkerPoint(selectedOrder)"
                    :icon="getMarkerIcon()">
                    <l-tooltip :options="tooltipOptions">
                      <!-- <p style="margin-bottom: 0px;">
                        <span>
                          <b>{{ getData('customer.name', selectedOrder) }}</b>
                        </span>
                      </p> -->
                      <p style="margin-bottom: 0px;">{{ getAddressLine() }}</p>
                    </l-tooltip>
                  </l-marker>
                </l-map>
              </div>
            </div>
            <div class="row">
              <div class="col p-0">
                <div class="d-flex justify-content-between align-items-baseline">
                  <h5 style="font-weight: bold;">Activity</h5>
                  <b-button variant="outline-primary" size="sm" @click="toggleActivity">{{ textButtonActivity }}</b-button>
                </div>
                <table class="ml-2 table-activity" v-if="showActivity">
                  <tr v-if="loadingHistory">Loading Activity...</tr>
                  <tr
                    v-else
                    v-for="(history, index) in orderHistoriesFormatted"
                    :key="`history-${index}`"
                  >
                    <td>
                      <div>
                        <span style="font-weight: bold;">{{ history.action_by }}</span>
                        <span v-html="history.action_name"></span>
                        <span v-if="history.extra">
                          <b-badge
                            v-if="history.extra"
                            :id="`history-${index}`"
                            style="cursor: pointer;"
                            title="Hover to Show Detail"
                          >
                            <i>{{ history.extra.type }}</i>
                          </b-badge>
                          <span v-if="parseInt(history.extra.detail.type) === 1">
                            with notes
                            <b>
                              {{ history.extra.detail.notes.join(', ') }}
                            </b>
                          </span>
                        </span>
                      </div>
                      <div class="text-muted small">{{ history.action_date }}</div>
                      <div :id="`history-popover-${index}`"></div>
                      <b-popover
                        v-if="history.extra"
                        :target="`history-${index}`"
                        :title="history.extra.type"
                        placement="top"
                        :container="`history-popover-${index}`"
                        title="Pictures"
                        triggers="hover focus"
                      >
                        <table>
                          <tr>
                            <td>
                              <b-carousel
                                style="text-shadow: 1px 1px 2px #333;"
                                fade
                                controls
                                indicators
                                background="#ababab"
                                v-model="slide"
                                :interval="0"
                              >
                                <b-carousel-slide v-for="image in history.extra.detail.images" :key="image">
                                  <img
                                    slot="img"
                                    class="d-block img-fluid mx-auto img-notes"
                                    :src="getImgUrl(image)"
                                  />
                                </b-carousel-slide>
                              </b-carousel>
                            </td>
                          </tr>
                        </table>
                      </b-popover>
                    </td>
                  </tr>
                </table>
              </div>
            </div>
          </div>
          <div class="col-md-2">
            <div class="row">
              <div class="col p-0">
                <h5 style="font-weight: bold;">Actions</h5>
              </div>
            </div>
            <div class="row">
              <div class="col p-0">
                <b-button-group vertical class="d-block">
                  <b-button variant="danger" class="mb-2" @click="deleteOrder" :disabled="isDeleteOrderDisabled">
                    <span class="pull-left">
                      <i class="fa fa-trash-o mr-2" aria-hidden="true"></i>Delete
                    </span>
                  </b-button>
                  <b-button v-if="possibleMoveOrder" id="popover-button-move" @click="showPopoverMove = !showPopoverMove" variant="outline-primary" class="mb-2">
                    <span class="pull-left">
                      <i class="fa fa-arrow-right mr-2" aria-hidden="true"></i>Move
                    </span>
                  </b-button>
                  <b-popover ref="popover" @show="onShowPopoverMove" @hidden="onHidePopoverMove" placement="bottom" :show.sync="showPopoverMove" target="popover-button-move" title="Select Schedule">
                    <b-list-group style="min-width: 250px;">
                      <b-list-group-item @click="selectMoveTo(board.key)" :class="`bg-${board.variant} mb-2 p-1 move-item`" v-for="board in targetMoveBoards" :key="board.key">
                        <strong>{{ board.title === 'Assigned' ? board.title : `Set as ${board.title}` }}</strong>
                        <b-row v-if="board.key === 'assigned'">
                          <b-col md="8" class="pr-0">
                            <b-form-select
                              @click.native.stop
                              v-model="assignTarget"
                              :options="optionsAssigned"
                              size="sm"
                            />
                          </b-col>
                          <b-col md="4">
                            <b-button size="sm" variant="warning" @click="onChangeAssignTarget">Update</b-button>
                          </b-col>
                        </b-row>
                      </b-list-group-item>
                    </b-list-group>
                    <b-button @click="hideMovePopover" class="pull-right mt-2 mb-2" size="sm" variant="outline-warning">Cancel</b-button>
                  </b-popover>
                </b-button-group>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
    <b-modal
      :title="titleScheduleModal"
      ref="createSchedule"
      width="70%"
      hide-footer
      centered
      size="lg"
    >
      <div class="container mb-4 text-md-left">
        <div class="row">
          <div class="col-md-6">
            <label>Vehicle Group</label>
            <multiselect
                name="vehicle_groups"
                v-validate="'required'"
                :class="{'multiselect-danger': errors.has('vehicle_groups')}"
                :max-height="100"
                v-model="selectedVehicleGroup"
                :options="vehicle_groups"
                label="name"
                track-by="id"
                @select="onChangeVehicleGroup"
                :disabled="isVehicleGroupOptionDisabled"
                placeholder="Select Vehicle Groups" class="mb-4"/>
          </div>
          <div class="col-md-6">
            <label>Vehicle</label>
            <multiselect
                name="vehicle"
                v-validate="'required'"
                :class="{'multiselect-danger': errors.has('vehicle')}"
                :max-height="100"
                v-model="form.vehicle"
                :options="filterredVehicles"
                label="reg_no"
                track-by="id"
                placeholder="Select vehicle" class="mb-4"
              />
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <label>Date</label>
            <b-form-input
              type="date"
              v-model="form.delivery_date"
              :min="today"
              v-validate="'required'"
            ></b-form-input>
          </div>
          <div class="col-md-6">
            <label>Shift</label>
            <b-form-select
              name="delivery_time"
              v-model="delivery_time"
              v-validate="'required'"
              :class="{'is-invalid': errors.has('delivery_time')}"
              :options="[
                {
                  text: 'Day',
                  value: '04:20',
                },
                {
                  text: 'Night',
                  value: '19:30',
                },
              ]"
            />
          </div>
        </div>
      </div>
      <hr>
      <b-button
        @click="cancelSaveSchedule"
        variant="outline-secondary"
        class="pull-right">Cancel</b-button>
      <b-button
        @click="saveSchedule"
        class="pull-left"
        :disbaled="disableButtonSave"
        variant="primary">{{ textButtonSave }}</b-button>
    </b-modal>
  </b-overlay>
</template>
<script>
import Multiselect from 'vue-multiselect';
import L from 'leaflet';
import { addDays, format, subDays, isAfter } from 'date-fns';
import { startCase, camelCase } from 'lodash';
import get from 'lodash/get';
import values from 'lodash/values';
import find from 'lodash/find';
import last from 'lodash/last';
import capitalize from 'lodash/capitalize';
import includes from 'lodash/includes';
import padStart from 'lodash/padStart';
import cloneDeep from 'lodash/cloneDeep';
import isUndefined from 'lodash/isUndefined';
import DatePicker from 'vue2-datepicker';
import BoardCard from '@/components/BoardCard';
import { LMap, LTileLayer, LMarker, LTooltip, LIcon, LCircle } from 'vue2-leaflet';

import * as Config from '@/config';

const MIN_DATE_JOB = 1;
const MAX_DATE_JOB = 14;

export default {
  components: {
    Multiselect,
    DatePicker,
    BoardCard,
    LMap,
    LTileLayer,
    LMarker,
    LTooltip,
    LIcon,
    LCircle,
  },
  data() {
    const today = format(new Date(), 'YYYY-MM-DD');
    return {
      today,
      query: '',
      busy: true,
      showPopoverMove: false,
      targetMoveBoards: [],
      assignTarget: 0,
      selectedOrder: null,
      boardKey: null,
      jobs: [],
      shipments: [],
      boards: [
        {
          key: 'unassign',
          title: 'Unassigned',
          variant: 'warning',
          group: {
            name: 'unassign',
            pull: ['assigned', 'completed', 'rejected'],
            put: ['assigned', 'rejected'],
          },
          items: [],
        },
        {
          key: 'assigned',
          title: 'Assigned',
          variant: 'primary',
          items: [],
        },
        {
          key: 'rejected',
          title: 'Unable to Process',
          variant: 'danger',
          group: {
            name: 'rejected',
            pull: ['unassign', 'completed', 'assigned'],
            put: false,
          },
          items: [],
        },
        {
          key: 'completed',
          title: 'Completed',
          variant: 'success',
          group: {
            name: 'completed',
            pull: false,
            put: ['unassign', 'rejected'],
          },
          items: [],
        },
      ],
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      center: [-6.17543846855061, 106.86290799928327],
      coordinates: [0, 0],
      zoom: 13,
      tooltipOptions: {
        permanent: true,
        direction: 'top',
        offset: L.point(0, -30),
      },
      bounds: L.latLngBounds(
        { lat: 51.476483373501964, lng: -0.14668464136775586 },
        { lat: 51.52948330894063, lng: -0.019140238291583955 },
      ),
      mapState: 'loading',
      titleScheduleModal: 'Create Schedule',
      textButtonSave: 'Save',
      isEdit: false,
      delivery_time: null,
      form: {
        number: '',
        vehicle: null,
        metadata: '',
        delivery_date: today,
        vehicle_group: '',
        driver_id: '',
      },
      selectedVehicleGroup: null,
      textButtonActivity: 'Hide Details',
      showActivity: true,
      timeoutCheckJobsInfo: 0,
      orderHistories: [],
      confirmationHistories: [],
      loadingHistory: false,
      saveOrders: [],
      extraType: [
        'Partially Completed',
        'With Exception',
      ],
      slide: 0,
      isVehicleGroupOptionDisabled: false,
      isDeleteOrderDisabled: false,
    };
  },
  created() {
    this.$store.dispatch('vehicles/LOAD_INFO').then(() => {
      this.$store.commit('vehicles/SET_QUERY_COUNT', this.count_vehicle_all);
      this.$store.dispatch('vehicles/LOAD_DATA');
    });
    this.$store.dispatch('shipment_md/shipments/LOAD_LOCATIONS');

    this.$store.commit('vehicle_group/CLEAR_QUERY');
    this.$store.commit('vehicle_group/SET_USE_SKIP', false);
    this.$store.commit('vehicle_group/SET_COMPANY_ID', this.myCompany);
    this.$store.dispatch('vehicle_group/LOAD_INFO').then(() => {
      this.$store.commit('vehicle_group/SET_QUERY_COUNT', this.count_vehicle_group);
      this.$store.dispatch('vehicle_group/LOAD_DATA').then(() => {
        this.loadJobsByRangeDate();
        this.loadCompaniesData().then(() => {
          if (!this.isEdit) {
            const defaultCompany = find(this.companiesData, { id: this.myCompany.toString() });
            this.form.company = defaultCompany;
            this.selectedCompany = defaultCompany;
          }
        });
      });
    });
  },
  mounted() {
    this.toggleSidebar(false);
  },
  computed: {
    myCompany() {
      return this.$store.getters.currentCompany;
    },
    vehicle_groups() {
      return this.$store.getters['vehicle_group/vehicle_groups'];
    },
    count_vehicle_group() {
      return this.$store.getters['vehicle_group/count_all'];
    },
    count_vehicle_all() {
      return this.$store.getters['vehicles/count_all'];
    },
    vehicles() {
      const vehicles = this.$store.getters['vehicles/vehicles'];
      vehicles.map(vehicle => this.addDriverOnVehicle(vehicle));
      return vehicles;
    },
    filterredVehicles() {
      const filterredVehicles = this.vehicles.filter((vehicle) => {
        const vehicleGroups = get(vehicle, 'groups', []);
        if (vehicleGroups.length && this.selectedVehicleGroup) {
          return vehicleGroups.find(vg =>
            parseInt(vg.id, 10) === parseInt(this.selectedVehicleGroup.id, 10));
        }
        return false;
      });
      return filterredVehicles;
    },
    detailedInfo() {
      return this.$store.getters['shipment_md/shipments/get_detail_data'];
    },
    possibleMoveOrder() {
      return ['unassign', 'assigned', 'rejected'].includes(this.boardKey);
    },
    optionsAssigned() {
      const options = [{ value: 0, text: 'Select target' }];
      const isExistsCrew = (current, target) => {
        for (let i = 0; i < target.length; i += 1) {
          for (let j = 0; j < current.length; j += 1) {
            if (parseInt(target[i], 10) === parseInt(current[j], 10)) {
              return true;
            }
          }
        }
        return false;
      };

      const orderGroups = get(this.selectedOrder, 'metadata.vehicle_groups', []);

      for (let i = 0; i < this.shipments.length; i += 1) {
        const dt = this.shipments[i];
        let vehicleGroups = get(dt, 'metadata.vehicle_group', '');
        vehicleGroups = vehicleGroups !== '' ? [vehicleGroups] : [];

        if (vehicleGroups.length === 0) {
          const vehicleGroup = this.getVehicleGroup(dt.vehicle);
          if (vehicleGroup) vehicleGroups.push(parseInt(vehicleGroup.id, 10));
        }

        if (!isExistsCrew(orderGroups, vehicleGroups)) continue;
        const vehicleRegNo = get(dt, 'vehicle.reg_no', '');
        const shipmentDate = format(get(dt, 'delivery_date'), 'DD MMM');
        const dTimes = {
          '04:20': 'Day',
          '19:30': 'Night',
        };
        const deliveryTime = get(dt, 'metadata.delivery_time', '');
        let textOption = `${vehicleRegNo} `;

        if (shipmentDate) {
          textOption += `- ${shipmentDate} `;
        }
        if (deliveryTime) {
          textOption += `- ${capitalize(dTimes[deliveryTime])}`;
        }

        options.push({ value: get(dt, 'id', ''), text: textOption });
      }
      return options;
    },
    boardTitle() {
      const board = this.boards.find(dt => dt.key === this.boardKey);
      return get(board, 'title', '');
    },
    boardVariant() {
      const board = this.boards.find(dt => dt.key === this.boardKey);
      return get(board, 'variant', '');
    },
    disableButtonSave() {
      return !this.form.vehicle || !this.form.delivery_date || !this.delivery_time;
    },
    orderHistoriesFormatted() {
      let idx = 0;
      const data = [];

      this.orderHistories.forEach((history) => { // eslint-disable-line
        let action = '';
        const { before, after, images } = history;
        const formatedActionDate = format(this.get(history, 'updated_at'), 'DD MMMM YYYY HH:mm:ss');
        const isBeforeShipment = this.getData('before.shipment_id', history, null) !== null;
        const isAfterShipment = this.getData('after.shipment_id', history, null) !== null;
        const reason = this.getData('after.metadata.confirmed.notes', history, '');
        const actionBy = this.get(history, 'action_by.full_name', '');
        const typeHistory = this.get(history, 'type');
        const shipmentIdAfter = this.getData('after.shipment_id', history, null);

        if (isBeforeShipment && !isAfterShipment) {
          action = 'move this order to unassign';
        } else if (!isBeforeShipment && isAfterShipment) {
          action = `assigned this order to shipment <b>(ID: ${shipmentIdAfter})</b>`;
        } else if (typeHistory === 'ORDER_CREATE') {
          action = 'created job';
        } else if (typeHistory === 'ORDER_UPDATE') {
          Object.keys(before).forEach((key) => { // eslint-disable-line
            const afterVal = this.get(after, key, '');

            if (key === 'metadata') {
              let metaBefore = get(before, 'metadata', null);
              metaBefore = metaBefore === null ? {} : metaBefore;
              let metaAfter = get(after, 'metadata', null);
              metaAfter = metaAfter === null ? {} : metaAfter;

              Object.keys(metaAfter).forEach((keyMeta) => { // eslint-disable-line
                let beforeValMeta = get(metaBefore, keyMeta, null);
                beforeValMeta = beforeValMeta === null ? {} : beforeValMeta;
                let afterValMeta = get(metaAfter, keyMeta, null);
                afterValMeta = afterValMeta === null ? {} : afterValMeta;
                const isChanged = beforeValMeta !== afterValMeta;

                if (keyMeta === 'status') {
                  const extraBefore = get(metaBefore, 'status.extra', false);
                  const extraAfter = get(metaAfter, 'status.extra', false);
                  if (extraAfter) {
                    if (extraBefore) {
                      const extraTypeBefore = get(extraBefore, 'type', null);
                      const extraTypeAfter = get(extraAfter, 'type', null);
                      if (extraTypeBefore === extraTypeAfter) return false;
                    }
                    extraAfter.images = images;
                    const orderExtraType = this.extraType[parseInt(extraAfter.type, 10)];
                    if (isUndefined(orderExtraType)) return false;
                    const extraData = {};
                    extraData.type = orderExtraType;
                    extraData.detail = extraAfter;
                    extraData.showExtra = false;

                    if (!data.find(d => d.action_date === formatedActionDate && d.action_name === 'has been set to ')) {
                      if (!isUndefined(this.confirmationHistories[idx])) {
                        extraData.detail.images = get(this.confirmationHistories[idx], 'after.metadata.images', []);
                        idx++;
                      }
                      data.push({
                        action_by: 'Order',
                        action_name: 'has been set to ',
                        action_date: formatedActionDate,
                        extra: extraData,
                      });
                    }
                  }
                }

                if (keyMeta === 'confirmed') {
                  const isConfirmed = !metaBefore.confirmed && metaAfter.confirmed;
                  const confirmedBy = get(metaAfter, 'confirmed.name', '');
                  if (isConfirmed) {
                    action = `complete this order with reason <b>${reason}</b>`;
                    data.push({
                      action_by: confirmedBy,
                      action_name: action,
                      action_date: formatedActionDate,
                    });
                    return false;
                  }
                }

                if (keyMeta === 'abort' || keyMeta === 'rejected') {
                  const isCancelled = (!metaBefore.abort && metaAfter.abort)
                    || (!metaBefore.rejected && metaAfter.rejected);
                  if (isCancelled) {
                    const cancellationReason = get(metaAfter, `${keyMeta}.reason`, '');
                    const cancelledBy = get(metaAfter, `${keyMeta}.name`, '');
                    action = `${keyMeta} this order with reason <b>${cancellationReason}</b>`;

                    const actionExists = data.find(d => d.action_name === action);
                    if (actionExists) return false;

                    data.push({
                      action_by: cancelledBy,
                      action_name: action,
                      action_date: formatedActionDate,
                    });
                  }
                  return false;
                }

                if (typeof afterValMeta !== 'string' || !isChanged) {
                  return false;
                }

                action = `update ${this.formatTextKey(keyMeta)} to <i class="text-muted">${afterValMeta}</i>`;
                data.push({
                  action_by: actionBy,
                  action_name: action,
                  action_date: formatedActionDate,
                });
              });
              return false;
            }

            action = `update ${this.formatTextKey(key)} to <i class="text-muted">${afterVal}</i>`;

            data.push({
              action_by: actionBy,
              action_name: action,
              action_date: formatedActionDate,
            });
          });

          return false;
        }

        data.push({
          action_by: actionBy,
          action_name: action,
          action_date: formatedActionDate,
        });
      });

      return data;
    },
    filterredBoards() {
      const query = this.query.toLowerCase();
      const boards = cloneDeep(this.boards);
      return boards.map((board) => {
        if (board.key === 'assigned') {
          board.items = board.items.map((bi) => {
            bi.items = bi.items.filter((bii) => {
              const isMatchNumber = get(bii, 'number', '')
                .toString().toLowerCase().indexOf(query) > -1;
              const isMatchAddress1 = get(bii, 'destination.metadata.address_line_1', '')
                .toString().toLowerCase().indexOf(query) > -1;
              const isMatchAddress2 = get(bii, 'destination.metadata.address_line_2', '')
                .toString().toLowerCase().indexOf(query) > -1;
              const isMatchDesc = get(bii, 'metadata.work_order_description', '')
                .toString().toLowerCase().indexOf(query) > -1;
              return isMatchNumber || isMatchAddress1 || isMatchAddress2 || isMatchDesc;
            });
            bi.items = bi.items.sort((a, b) => {
              if (a.number > b.number) return 1;
              if (a.number < b.number) return -1;
              return 0;
            });
            return bi;
          });
        } else {
          board.items = board.items.filter((bi) => {
            const isMatchNumber = get(bi, 'number', '')
              .toString().toLowerCase().indexOf(query) > -1;
            const isMatchAddress1 = get(bi, 'destination.metadata.address_line_1', '')
              .toString().toLowerCase().indexOf(query) > -1;
            const isMatchAddress2 = get(bi, 'destination.metadata.address_line_2', '')
              .toString().toLowerCase().indexOf(query) > -1;
            const isMatchDesc = get(bi, 'metadata.work_order_description', '')
              .toString().toLowerCase().indexOf(query) > -1;
            return isMatchNumber || isMatchAddress1 || isMatchAddress2 || isMatchDesc;
          });
          board.items = board.items.sort((a, b) => {
            if (a.number > b.number) return 1;
            if (a.number < b.number) return -1;
            return 0;
          });
        }
        if (board.key === 'completed') {
          const today = format(subDays(new Date(), 7), 'YYYY-MM-DD HH:mm:ss');
          board.items = board.items.filter((item) => {
            const confirmations = item.confirmations.filter(c => c.type === 'ORDER' && c.key === 'customer');
            const confirmation = last(confirmations) || {};
            let createdAt = get(confirmation, 'created_at', null);
            if (createdAt === null) return false;
            createdAt = format(createdAt, 'YYYY-MM-DD HH:mm:ss');
            return isAfter(createdAt, today);
          });
        }
        return board;
      });
    },
  },
  watch: {
    detailedInfo(data) {
      const { form } = this;
      let { vehicle } = data;
      vehicle = this.addDriverOnVehicle(vehicle);
      const { metadata } = data;
      form.delivery_date = format(data.delivery_date, 'YYYY-MM-DD');
      form.metadata = metadata;
      form.vehicle = vehicle;
      this.delivery_time = get(metadata, 'delivery_time', '');
      form.selectedVehicle = vehicle;
    },
  },
  methods: {
    get,
    addDriverOnVehicle(vehicle) {
      let regNo = vehicle.reg_no;
      const driverName = get(vehicle, 'driver.name', false);
      if (driverName) regNo += ` (${driverName})`;
      vehicle.reg_no = regNo;
      return vehicle;
    },
    getData(key, data, def = '') {
      const field = get(data, key, def);
      return field;
    },
    toggleSidebar(show) {
      this.$store.commit('SHOW_HIDE_BREADCRUMB', show);
      this.$store.commit('SHOW_HIDE_FOOTER', show);
      if (show) {
        this.$store.commit('RESTORE_SIDEBAR');
        document.body.classList.remove('sidebar-minimized');
      } else {
        this.$store.commit('MINIMIZE_SIDEBAR');
        document.body.classList.add('sidebar-minimized');
      }
    },
    isAssigned(board) {
      return board.key === 'assigned';
    },
    isAssignedNotEmpty(board) {
      return board.items.length > 0;
    },
    getDeliveryDate(item) {
      const dTimes = {
        '04:20': 'Day',
        '19:30': 'Night',
      };
      let deliveryDate = get(item, 'delivery_date', '');
      let shiftText = '';
      const deliveryTime = get(item, 'delivery_time', '');

      if (deliveryTime) {
        const shiftIcon = dTimes[deliveryTime] === 'Day'
          ? '<i class="fa fa-sun-o" aria-hidden="true"></i>'
          : '<i class="fa fa-moon-o" aria-hidden="true"></i>';
        shiftText += `${shiftIcon} ${capitalize(dTimes[deliveryTime])}`;
      }

      deliveryDate = deliveryDate ? format(deliveryDate, 'DD MMM') : '';
      deliveryDate = deliveryTime ? `${deliveryDate}, ${shiftText}` : deliveryDate;
      return deliveryDate;
    },
    getDriverName(item) {
      const driverName = get(item, 'driver.name', '');
      return driverName;
    },
    getCrew(item, mod = true) {
      const crews = get(item, 'crews', []);
      let name = crews.length ? crews[0].name : '';
      if (mod && name.length > 6) {
        name = name.match(/\b[A-Z]/g);
        name = name ? name.join('') : '';
      }
      return name;
    },
    getShipmentState(item) {
      let state = get(item, 'state', '');
      state = includes(['CREATED', 'READY_TO_LOAD', 'FINISH_LOADING'], state) ? 'Preparation' : 'On Process';
      return state;
    },
    onChangeVehicleGroup() {
      this.form.vehicle = null;
    },
    onClickCreateShipment() {
      this.isVehicleGroupOptionDisabled = false;
      this.selectedVehicleGroup = null;
      this.titleScheduleModal = 'Create Schedule';
      this.$refs.createSchedule.show();
    },
    onClickEditShipment(item) {
      this.isEdit = true;
      const orders = get(item, 'items', []);
      this.isVehicleGroupOptionDisabled = orders.length > 0;
      const crews = get(item, 'crews', []);
      this.selectedVehicleGroup = crews.length ? crews[0] : {};
      this.$store.commit('shipment_md/shipments/SET_DETAIL_ID', item.shipment_id);
      this.$store.dispatch('shipment_md/shipments/LOAD_DETAIL_DATA').then(() => {
        this.titleScheduleModal = 'Update Schedule';
        this.$refs.createSchedule.show();
      });
    },
    onClickDeleteShipment(item) {
      const self = this;
      this.$swal({
        title: 'Delete Confirmation',
        html: 'Do you want to remove all the jobs as well?',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        showCloseButton: true,
        onBeforeOpen: () => {
          const vm = self;
          const actions = document.querySelector('.swal2-actions');
          actions.querySelector('.swal2-cancel').style.backgroundColor = '#f7b900';

          const cancelButton = document.createElement('button');
          cancelButton.innerText = 'Cancel';
          cancelButton.classList.add('swal2-styled');
          cancelButton.classList.add('swal2-cancel');
          cancelButton.style.display = 'inline-block';
          cancelButton.addEventListener('click', () => {
            vm.$swal.close();
          });
          actions.appendChild(cancelButton);

          actions.querySelectorAll('.swal2-styled').forEach((e) => {
            e.style.width = '120px';
          });
        },
      }).then((result) => {
        if (result.value) {
          Promise.all([
            this.$store.dispatch('shipment_md/orders/DELETE_DATA_BY_SHIPMENT', item.shipment_id),
            this.$store.dispatch('shipment_md/shipments/DELETE_DATA', item.shipment_id),
          ])
            .then(() => {
              this.$swal('Success', 'Successfully delete schedule.', 'info').then(() => {
                this.loadJobsByRangeDate();
              });
            });
        } else if (result.dismiss) {
          const payload = { shipment_id: item.shipment_id };
          const promises = [];
          item.items.forEach((i) => {
            payload.order_id = i.id;
            promises.push(payload);
            promises.push(this.$store.dispatch('shipment_md/shipments/REMOVE_SHIPMENT_ORDER', payload));
          });
          Promise.all([
            this.$store.dispatch('shipment_md/shipments/DELETE_DATA', item.shipment_id),
            ...promises,
          ])
            .then(() => {
              this.$swal('Success', 'Successfully delete schedule.', 'info').then(() => {
                this.loadJobsByRangeDate();
              });
            });
        }
      });
    },
    loadJobsInfo() {
      return this.$store.dispatch('shipment_md/orders/LOAD_JOBS_INFO', {
        start_date: format(subDays(new Date(), MAX_DATE_JOB), 'YYYY-MM-DD'),
        end_date: format(addDays(new Date(), MIN_DATE_JOB), 'YYYY-MM-DD'),
      }).then((data) => {
        let reloadJobData = false;

        data.forEach((jobInfo) => { // eslint-disable-line
          const { type, count } = jobInfo;
          const jobData = this.jobs.find(dt => dt.type === type);
          if (!jobData) { return false; }

          const isSameCount = count === jobData.count;
          if (!isSameCount) {
            reloadJobData = true;
            return true;
          }
        });

        if (reloadJobData) {
          this.loadJobsByRangeDate();
          return Promise.resolve(data);
        }

        clearTimeout(this.timeoutCheckJobsInfo);
        this.timeoutCheckJobsInfo = setTimeout(() => {
          this.loadJobsInfo();
        }, 10e3);

        return Promise.resolve(data);
      });
    },
    loadJobsByRangeDate() {
      this.busy = true;

      this.$store.commit('shipment_md/shipments/CLEAR_QUERY');
      this.$store.commit('shipment_md/shipments/SET_STATE_FILTER', ['CREATED', 'READY_TO_LOAD', 'FINISH_LOADING', 'ON_PROCESS']);
      return this.$store.dispatch('shipment_md/shipments/LOAD_INFO').then(() => {
        const shipment_count = this.$store.getters['shipment_md/shipments/count_all'];
        this.$store.commit('shipment_md/shipments/SET_QUERY_COUNT', shipment_count);
        Promise.all([
          this.$store.dispatch('shipment_md/orders/LOAD_JOBS', {
            start_date: format(subDays(new Date(), MAX_DATE_JOB), 'YYYY-MM-DD'),
            end_date: format(addDays(new Date(), MIN_DATE_JOB), 'YYYY-MM-DD'),
          }),
          this.$store.dispatch('shipment_md/shipments/LOAD_SHIPMENTS_BY_RANGE_DATE2', {
            start_date: format(subDays(new Date(), MAX_DATE_JOB), 'YYYY-MM-DD'),
            end_date: format(addDays(new Date(), MIN_DATE_JOB), 'YYYY-MM-DD'),
            state_filter_or: ['CREATED', 'READY_TO_LOAD', 'FINISH_LOADING', 'ON_PROCESS'],
          }),
        ])
          .then(([jobs, shipments]) => {
            this.shipments = shipments;
            this.jobs = jobs;
            this.boards.map((board) => {
              const key = board.key === 'rejected' ? 'not_processed' : board.key;
              const job = jobs.find(j => j.type === key);
              if (key === 'assigned') {
                const shipmentJobs = {};
                job.orders.forEach((o) => {
                  o.shipment = shipments.find(s => s.orders.find(os => os.id === o.id));
                  const shipmentId = get(o, 'shipment.id');
                  let crews = get(o, 'shipment.metadata.vehicle_group', '');
                  crews = crews !== '' ? [this.vehicle_groups.find(vg => parseInt(vg.id, 10) === crews)] : [];

                  if (crews.length === 0) {
                    if (o.shipment) {
                      const vehicleGroup = this.getVehicleGroup(o.shipment.vehicle);
                      if (vehicleGroup) crews.push(vehicleGroup);
                    }
                  }

                  if (!shipmentJobs[shipmentId]) {
                    shipmentJobs[shipmentId] = {
                      shipment_id: shipmentId,
                      delivery_date: get(o, 'shipment.delivery_date'),
                      delivery_time: get(o, 'shipment.metadata.delivery_time'),
                      driver: get(o, 'shipment.driver', {}),
                      state: get(o, 'shipment.state', ''),
                      title: get(o, 'shipment.vehicle.reg_no', ''),
                      group: {
                        name: 'assigned',
                        pull: ['unassign', 'assigned'],
                        put: ['unassign', 'assigned', 'rejected'],
                      },
                      items: [],
                      crews,
                    };
                  }
                  shipmentJobs[shipmentId].items.push(o);
                });
                board.items = values(shipmentJobs);
              } else {
                const getCrewOrder = (ord) => {
                  let vehicleGroup = get(ord, 'metadata.vehicle_groups', []);
                  vehicleGroup = vehicleGroup.length ? vehicleGroup[0] : null;
                  if (vehicleGroup != null) {
                    vehicleGroup = this.vehicle_groups.find(vg =>
                      parseInt(vg.id, 0) === parseInt(vehicleGroup, 10));
                    if (vehicleGroup) vehicleGroup = get(vehicleGroup, 'name', null);
                  }
                  return vehicleGroup;
                };
                for (let i = 0; i < job.orders.length; i += 1) {
                  job.orders[i].crew = getCrewOrder(job.orders[i]);
                }
                board.items = job.orders;
              }
              return board;
            });
            this.shipments.forEach((shipment) => {
              const orders = get(shipment, 'orders', []);
              if (orders.length === 0) {
                let crews = get(shipment, 'metadata.vehicle_group', '');
                crews = crews !== '' ? [this.vehicle_groups.find(vg => parseInt(vg.id, 10) === crews)] : [];

                if (crews.length === 0) {
                  const vehicleGroup = this.getVehicleGroup(shipment.vehicle);
                  if (vehicleGroup) crews.push(vehicleGroup);
                }

                const boardIndex = this.boards.findIndex(board => board.key === 'assigned');
                this.boards[boardIndex].items.push({
                  shipment_id: get(shipment, 'id', ''),
                  delivery_date: get(shipment, 'delivery_date'),
                  delivery_time: get(shipment, 'metadata.delivery_time'),
                  state: get(shipment, 'state', ''),
                  driver: get(shipment, 'driver', {}),
                  title: get(shipment, 'vehicle.reg_no', ''),
                  group: {
                    name: 'assigned',
                    pull: ['unassign', 'assigned'],
                    put: ['unassign', 'assigned', 'rejected'],
                  },
                  items: [],
                  crews,
                });
              }
            });
            this.loadJobsInfo();
          })
          .finally(() => {
            setTimeout(() => {
              this.busy = false;
              this.saveWorkOrderDetails();
            }, 1e3);
          });
      });
    },
    saveWorkOrderDetails() {
      const promises = [];
      this.saveOrders.forEach((order) => {
        const promise = this.$store.dispatch('shipment_md/orders/UPDATE_DATA', {
          id: order.id,
          metadata: order.metadata,
        });
        promises.push(promise);
      });
      Promise.all(promises)
        .then(() => {
          this.saveOrders = [];
        });
    },
    runAndReloadShipmentApi(api = '', payload = {}) {
      return this.$store.dispatch(`shipment_md/shipments/${api}`, payload)
        .then(() => {
          this.loadJobsByRangeDate();
        })
        .finally(() => {
          this.busy = false;
        });
    },
    moveOrder(context) {
      const orderId = context.item.id.replace('order-', '');
      const isFromShipment = context.from.id.indexOf('assigned') > -1;
      const isToShipment = context.to.id.indexOf('assigned') > -1;

      if (context.from.id === context.to.id) return false;
      if (this.busy) return false;

      const currentOrdersJob = this.jobs.find(j => j.orders.find(o => o.id === orderId)) || {};
      const currentOrderData = get(currentOrdersJob, 'orders', []).find(o => o.id === orderId);
      const orderCrews = get(currentOrderData, 'metadata.vehicle_groups', []);

      if (orderCrews.length === 0) {
        this.cancelMove(context);
        return false;
      }

      let api = '';
      const payload = {
        order_id: orderId,
      };

      const isExistsCrew = (current, target) => {
        for (let i = 0; i < target.length; i += 1) {
          for (let j = 0; j < current.length; j += 1) {
            if (parseInt(target[i], 10) === parseInt(current[j], 10)) {
              return true;
            }
          }
        }
        return false;
      };

      if (isFromShipment && isToShipment) {
        api = 'MOVE_SHIPMENT_ORDER';
        payload.source_shipment_id = context.from.id.replace('assigned-', '');
        payload.target_shipment_id = context.to.id.replace('assigned-', '');

        const targetShipment = this.shipments.find(i =>
          i.id === payload.target_shipment_id);
        let targetCrews = get(targetShipment, 'metadata.vehicle_group', '');
        targetCrews = targetCrews !== '' ? [targetCrews] : [];

        if (targetCrews.length === 0) {
          const vehicleGroup = this.getVehicleGroup(targetShipment.vehicle);
          if (vehicleGroup) targetCrews.push(parseInt(vehicleGroup.id, 10));
        }

        let crewValid = false;

        if (targetCrews.length) {
          const isCrewMatch = isExistsCrew(orderCrews, targetCrews);
          if (isCrewMatch) {
            crewValid = true;
          }
        }

        if (!crewValid) {
          this.cancelMove(context);
          return false;
        }
      } else if (isFromShipment) {
        api = 'REMOVE_SHIPMENT_ORDER';
        payload.shipment_id = context.from.id.replace('assigned-', '');
      } else if (isToShipment) {
        api = 'ADD_SHIPMENT_ORDER';
        payload.shipment_id = context.to.id.replace('assigned-', '');

        const targetShipment = this.shipments.find(i =>
          i.id === payload.shipment_id);
        let targetCrews = get(targetShipment, 'metadata.vehicle_group', '');
        targetCrews = targetCrews !== '' ? [targetCrews] : [];

        if (targetCrews.length === 0) {
          const vehicleGroup = this.getVehicleGroup(targetShipment.vehicle);
          if (vehicleGroup) targetCrews.push(parseInt(vehicleGroup.id, 10));
        }

        let crewValid = false;

        if (targetCrews.length) {
          const isCrewMatch = isExistsCrew(orderCrews, targetCrews);
          if (isCrewMatch) {
            crewValid = true;
          }
        }

        if (!crewValid) {
          this.cancelMove(context);
          return false;
        }
      } else if (context.from.id.indexOf('rejected') > -1) {
        if (context.to.id.indexOf('unassign') > -1) {
          return this.removeFromCancelled(orderId);
        }
      }

      this.busy = true;

      if (api) {
        const promises = [];
        promises.push(this.runAndReloadShipmentApi(api, payload));

        if (currentOrderData.confirmations) {
          for (let i = 0; i < currentOrderData.confirmations.length; i += 1) {
            promises.push(this.removeOrderConfirmation(currentOrderData.confirmations[i].id));
          }
        }

        return Promise.all(promises);
      }

      return this.setFinishOrder(orderId, context);
    },
    removeOrderConfirmation(confirmationId) {
      return this.$store.dispatch('shipment_md/order_status/REMOVE_ORDER_CONFIRMATION', { id: confirmationId });
    },
    removeFromCancelled(orderId) {
      this.busy = true;
      const job = this.jobs.find(j => j.type === 'not_processed');
      const orders = get(job, 'orders', []);
      const order = orders.find(o => o.id === orderId);
      const rejected = get(order, 'metadata.rejected', false);
      const key = rejected ? 'rejected' : 'abort';
      const promises = [
        this.$store.dispatch('shipment_md/orders/REMOVE_METADATA_KEY', { id: orderId, key }),
        this.$store.dispatch('shipment_md/orders/REMOVE_METADATA_KEY', { id: orderId, key: 'metadata.status.extra' }),
      ];

      if (order.confirmations) {
        for (let i = 0; i < order.confirmations.length; i += 1) {
          promises.push(this.removeOrderConfirmation(order.confirmations[i].id));
        }
      }

      return Promise.all(promises)
        .then(() => {
          this.runAndReloadShipmentApi('REMOVE_SHIPMENT_ORDER', { order_id: order.id, shipment_id: order.shipment.id });
        });
    },
    cancelMove(context) {
      context.from.appendChild(context.item);
    },
    setFinishOrder(orderId, context = null) {
      return new Promise((resolve) => {
        this.$swal({
          title: 'Please Give Reason',
          input: 'text',
          inputLabel: 'Reason',
          inputPlaceholder: 'Enter Your Reason',
        })
          .then((result) => {
            if (result.value) {
              if (result.value !== '') {
                return this.$store.dispatch('shipment_md/orders/FINISH_ORDER', {
                  order_id: orderId,
                  reason: result.value,
                })
                  .then(() => {
                    this.loadJobsByRangeDate();
                  })
                  .finally(() => {
                    this.busy = false;
                    resolve();
                  });
              }
            }

            if (context) {
              context.from.appendChild(context.item);
            }
            return this.loadJobsByRangeDate()
              .then(() => {
                this.loadJobsInfo();
                this.busy = false;
                resolve();
              });
          });
      });
    },
    clickOrder(order, boardKey) {
      this.$refs.orderDetails.show();
      this.isDeleteOrderDisabled = order.shipment !== null;
      this.selectedOrder = order;
      this.boardKey = boardKey;

      const { destination } = order;
      const { longitude, latitude } = destination;

      this.bounds = L.latLngBounds([{ longitude, latitude }].map(pt => (
        { lat: pt.latitude, lng: pt.longitude }
      )));
      this.mapState = 'loading';
      setTimeout(() => {
        this.mapState = 'ready';
      }, 5e2);

      this.loadingHistory = true;

      const promises = [
        this.$store.dispatch('shipment_md/orders/LOAD_JOB_HISTORY', order.id),
        this.$store.dispatch('shipment_md/orders/LOAD_CONFIRMATION_ORDER_HISTORY', order.id),
      ];

      Promise.all(promises)
        .then(([history, confirmations]) => {
          this.confirmationHistories = confirmations;
          this.orderHistories = history;
          setTimeout(() => {
            this.loadingHistory = false;
            this.showActivity = true;
            this.slide = 0;
          }, 5e2);
        });
    },
    formatTextKey(key) {
      const toPopoper = text => startCase(camelCase(text));
      return toPopoper(key.replaceAll('_', ' '));
    },
    getMapStyles() {
      const height = window.innerHeight - (window.innerHeight * 0.6);
      const styles = `height: ${height}px; width: 100%;`;
      return styles;
    },
    getMarkerPoint(data) {
      const { destination } = data;
      const { longitude, latitude } = destination;
      return {
        lat: latitude,
        lng: longitude,
      };
    },
    getMarkerIcon() {
      const icon = L.icon({
        iconUrl: 'https://unpkg.com/leaflet@1.2.0/dist/images/marker-icon.png',
        iconSize: [25, 41],
        iconAnchor: [12.5, 32],
      });
      return icon;
    },
    onMapReady() {
      setTimeout(() => {
        this.$refs.map.mapObject.invalidateSize();
        this.$refs.map.setZoom(18);
      }, 1e2);
    },
    loadCompaniesData() {
      return this.$store.dispatch('companies/LOAD_SIMPLE_DATA', { tenants: 0 }).then((data) => {
        const companies = data || [];
        this.companiesData = companies;
      });
    },
    createShipmentNumber() {
      const today = format(new Date(), 'YYYY-MM-DD');
      const todayShipments = this.shipments.filter(shipment => shipment.delivery_date === today);
      const prefix = this.form.delivery_date.split('-').join('');
      return `${prefix}${padStart(todayShipments.length + 1, 3, '0')}`;
    },
    saveSchedule() {
      this.validate().then((valid) => {
        if (valid) {
          if (this.busy) return false;
          this.busy = true;
          this.textButtonSave = 'Saving...';
          this.form.number = this.createShipmentNumber();
          this.form.delivery_date = format(this.form.delivery_date, 'YYYY-MM-DD');
          if (this.form.metadata === '') {
            this.form.metadata = {
              delivery_time: this.delivery_time,
              vehicle_group: parseInt(this.selectedVehicleGroup.id, 10),
            };
          } else {
            if (this.form.metadata === null) this.form.metadata = {};
            this.form.metadata.delivery_time = this.delivery_time;
            this.form.metadata.vehicle_group = parseInt(this.selectedVehicleGroup.id, 10);
          }
          const vehicle = this.vehicles.find(v =>
            parseInt(v.id, 10) === parseInt(this.form.vehicle.id, 10));
          this.form.driver_id = get(vehicle, 'driver.id', null);
          const action = this.isEdit ? 'UPDATE_DATA' : 'CREATE_COPP_SCHEDULE';
          this.$store.dispatch(`shipment_md/shipments/${action}`, this.form).then(() => {
            this.textButtonSave = 'Success';
            setTimeout(() => {
              this.textButtonSave = 'Save';
              this.loadJobsByRangeDate().then(() => {
                this.busy = false;
                this.cancelSaveSchedule();
                this.$refs.createSchedule.hide();
              });
            }, 2e3);
          });
        }
        return false;
      });
    },
    cancelSaveSchedule() {
      this.$refs.createSchedule.hide();
      this.titleScheduleModal = 'Create Schedule';
      this.delivery_time = null;
      this.form = {
        number: '',
        vehicle: null,
        metadata: '',
        delivery_date: '',
        vehicle_group: '',
        driver_id: '',
      };
    },
    validate() {
      return this.$validator.validateAll();
    },
    toggleActivity() {
      this.showActivity = !this.showActivity;
      this.textButtonActivity = this.showActivity ? 'Hide Details' : 'Show Details';
    },
    deleteOrder() {
      return this.$swal({
        title: 'Delete Confirmation',
        html: 'Are you sure want to <b>delete</b> this job?',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        showCloseButton: true,
      }).then((result) => {
        if (result.value) {
          this.$store.dispatch('shipment_md/orders/DELETE_DATA', this.selectedOrder.id).then(() => {
            setTimeout(() => {
              this.$swal('Success', 'Successfully delete job.', 'info').then(() => {
                this.$refs.orderDetails.hide();
                setTimeout(() => {
                  this.loadJobsByRangeDate();
                }, 1e3);
              });
            }, 1e3);
          });
        }
      });
    },
    onShowPopoverMove() {
      let allowedtargets = [];

      switch (this.boardKey) {
        case 'unassign':
          allowedtargets = ['assigned', 'completed'];
          this.assignTarget = 0;
          break;
        case 'assigned':
          allowedtargets = ['assigned', 'unassign'];
          this.assignTarget = get(this.selectedOrder, 'shipment.id', 0);
          break;
        case 'rejected':
          allowedtargets = ['unassign', 'completed', 'assigned'];
          this.assignTarget = get(this.selectedOrder, 'shipment.id', 0);
          break;
        default:
          break;
      }

      this.targetMoveBoards = this.boards.filter(dt => allowedtargets.includes(dt.key));
    },
    onHidePopoverMove() {
      this.targetMoveBoards = [];
    },
    selectMoveTo(target) {
      if (target === 'assigned') {
        return Promise.resolve();
      }

      const orderId = get(this.selectedOrder, 'id');
      const shipmentId = get(this.selectedOrder, 'shipment.id');

      if (this.boardKey === 'rejected' && target !== 'completed') {
        this.removeFromCancelled(orderId);
      }

      this.$refs.orderDetails.hide();
      this.busy = true;

      if (target === 'unassign') {
        return this.$store.dispatch('shipment_md/shipments/REMOVE_SHIPMENT_ORDER', {
          order_id: orderId,
          shipment_id: shipmentId,
        })
          .then(() => {
            this.loadJobsByRangeDate();
          })
          .finally(() => {
            this.busy = false;
          });
      }

      return this.setFinishOrder(orderId);
    },
    hideMovePopover() {
      this.$refs.popover.$emit('close');
    },
    onChangeAssignTarget() {
      const shipmentId = this.assignTarget;
      const payloads = {};
      const orderId = get(this.selectedOrder, 'id');
      const sourceShipmentId = get(this.selectedOrder, 'shipment.id', 0);
      const api = sourceShipmentId === 0 ? 'ADD_SHIPMENT_ORDER' : 'MOVE_SHIPMENT_ORDER';
      this.busy = true;
      this.$refs.orderDetails.hide();

      if (sourceShipmentId === 0) {
        payloads.order_id = orderId;
        payloads.shipment_id = shipmentId;
      } else {
        payloads.order_id = orderId;
        payloads.source_shipment_id = sourceShipmentId;
        payloads.target_shipment_id = shipmentId;
      }

      const promises = [];
      promises.push(this.$store.dispatch(`shipment_md/shipments/${api}`, payloads));

      if (this.selectedOrder.confirmations) {
        for (let i = 0; i < this.selectedOrder.confirmations.length; i += 1) {
          promises.push(this.removeOrderConfirmation(this.selectedOrder.confirmations[i].id));
        }
      }

      return Promise.all(promises)
        .then(() => {
          this.loadJobsByRangeDate();
        })
        .finally(() => {
          this.busy = false;
        });
    },
    getAddressLine() {
      let addressLine1 = get(this.selectedOrder, 'destination.metadata.address_line_1', '-');
      const addressLine2 = get(this.selectedOrder, 'destination.metadata.address_line_2', '');
      if (addressLine1 !== '-' && addressLine2 !== '') {
        addressLine1 += ` ${addressLine2}`;
      }
      const name = get(this.selectedOrder, 'destination.name', '-');
      return addressLine1 !== '-' ? addressLine1 : name;
    },
    isValidHttpUrl(string) {
      let url;
      try {
        url = new URL(string.trim());
      } catch (_) {
        return false;
      }
      return url.protocol === 'http:' || url.protocol === 'https:';
    },
    showExtra() {
      const extraType = get(this.selectedOrder, 'metadata.status.extra.type', false);
      const isExtra = extraType !== false;
      return isExtra && this.extraType[parseInt(extraType, 10)];
    },
    getExtra() {
      const extraType = get(this.selectedOrder, 'metadata.status.extra.type', 0);
      return this.extraType[parseInt(extraType, 10)];
    },
    getImgUrl(file) {
      const imgUrl = `${get(Config, 'default.tmsGraphqlApiUrl')}/uploads/images/${file}`;
      return imgUrl;
    },
    getVehicleGroup(vehicle) {
      const currentVehicle = this.vehicles.find(v =>
        parseInt(v.id, 10) === parseInt(vehicle.id, 10));
      const vehicleGroup = get(currentVehicle, 'groups.0', {});
      return vehicleGroup;
    },
  },
  beforeDestroy() {
    clearTimeout(this.timeoutCheckJobsInfo);
    this.toggleSidebar(true);
  },
};
</script>

<style scoped>
#order-schedule >>> .card-body {
    padding: 0.45rem 0.45rem 0 0.45rem;
}
#order-schedule >>> .dropdown-button > .dropdown-toggle {
  padding: 0 !important;
  color: #29363d !important;
  margin-top: 0px !important;
}
#order-schedule >>> .mx-input {
  border-radius: 5px !important;
}
.sweet-modal-overlay >>> .sweet-modal .sweet-title {
  padding: 20px;
}
.sweet-modal-overlay >>> .sweet-content {
  padding: 15px;
}
.table-detail td:first-child,
.table-activity td {
  white-space: nowrap;
  vertical-align: baseline;
}
.table-detail td:first-child {
  padding-right: 10px;
}
.btn-create {
  border-radius: 5px;
}
.move-item {
  cursor: pointer;
}
.advance--order-detail {
  font-size: 12px;
}
.swal2-styled.swal2-warning {
  background-color: #f7b900 !important;
}
.swal2-styled.swal2-default {
  background-color: #aaa !important;
}
@media (max-width: 960px) {
  .mx-datepicker {
    width: 100%;
  }
}
.crew {
  padding-top: 5px;
}
.icon-close-append {
  background: transparent;
  cursor: pointer;
}
.job-counter {
  position: absolute;
  top: 5px;
  right: 5px;
}

.img-notes {
  height: 200px;
  width: 200px;
  cursor: pointer;
}

.td-notes {
  vertical-align: top !important;
  padding-right: 20px;
}
</style>
<style lang="sass" scoped>
  @import "~leaflet/dist/leaflet.css";
</style>



// WEBPACK FOOTER //
// src/views/pages/shipment_md/orders/OrderSchedule.vue